import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Select,
  Spin,
  Alert,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Progress,
} from 'antd';
import {
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  getDailyHoldingsByAccount,
  getAccountList,
  formatCurrency,
  formatPercentage,
} from '../services/dashboardService';

const { Title, Text } = Typography;
const { Option } = Select;

const AccountHoldingsCard = ({ hideAmounts = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [holdingsData, setHoldingsData] = useState(null);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    fetchAccounts();
    
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (selectedAccount && isMounted) {
      fetchAccountHoldings(selectedAccount);
    }
  }, [selectedAccount, isMounted]);

  const fetchAccounts = async () => {
    try {
      const response = await getAccountList();
      // console.log('Accounts API response:', response); // 디버깅용
      
      // API 응답 구조 확인 - {count: 7, results: [...]} 형태
      let accountsData = [];
      if (response && response.results && Array.isArray(response.results)) {
        accountsData = response.results;
      } else if (Array.isArray(response)) {
        accountsData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        accountsData = response.data;
      }
      
      // active가 true인 계좌만 필터링
      const activeAccounts = accountsData.filter(account => account.active === true);
      
      // console.log('All accounts data:', accountsData); // 디버깅용
      // console.log('Active accounts only:', activeAccounts); // 디버깅용
      setAccounts(activeAccounts);
      
      // 첫 번째 active 계좌를 기본 선택
      if (activeAccounts.length > 0) {
        setSelectedAccount(activeAccounts[0].id);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('계좌 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const fetchAccountHoldings = async (accountId) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getDailyHoldingsByAccount(accountId);
      
      // API 응답 구조에 맞게 데이터 처리
      if (data && data.results) {
        // 선택된 계좌 정보 찾기
        const selectedAccountInfo = accounts.find(acc => acc.id === accountId);
        
        if (selectedAccountInfo) {
          // 선택된 계좌의 보유 종목만 필터링 (계좌 이름으로 매칭)
          const accountHoldings = data.results.filter(holding => 
            holding.account_name === selectedAccountInfo.account_name
          );
          
          setHoldingsData({
            holdings: accountHoldings,
            count: accountHoldings.length
          });
        } else {
          setHoldingsData({ holdings: [], count: 0 });
        }
      } else {
        setHoldingsData({ holdings: [], count: 0 });
      }
    } catch (error) {
      console.error('Error fetching account holdings:', error);
      setError('보유 현황을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId);
  };

  // 테이블 컬럼 정의
  const columns = [
    {
      title: '종목명',
      dataIndex: 'stock_name',
      key: 'stock_name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.stock_symbol}</div>
        </div>
      ),
    },
    {
      title: '보유수량',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (value) => (
        <Text strong>{value?.toLocaleString()}주</Text>
      ),
    },
    {
      title: '평균매수가',
      dataIndex: 'avg_buy_price',
      key: 'avg_buy_price',
      align: 'right',
      render: (value) => hideAmounts ? '******' : formatCurrency(value),
    },
    {
      title: '현재가치',
      dataIndex: 'market_value',
      key: 'market_value',
      align: 'right',
      render: (value) => (
        <Text strong style={{ color: '#1890ff' }}>
          {hideAmounts ? '******' : formatCurrency(value || 0)}
        </Text>
      ),
    },
    {
      title: '당일 손익',
      key: 'daily_pnl',
      align: 'right',
      render: (_, record) => {
        // prev_day_change와 quantity가 있는지 확인
        const change = record.prev_day_change;
        const quantity = record.quantity;
        
        if (change === null || change === undefined || !quantity) {
          return <Text type="secondary">-</Text>;
        }
        
        const dailyPnL = change * quantity;
        const isPositive = dailyPnL >= 0;
        const color = isPositive ? '#52c41a' : '#ff4d4f';
        const icon = isPositive ? <RiseOutlined /> : <FallOutlined />;
        
        return (
          <div style={{ color }}>
            <div style={{ fontWeight: 'bold' }}>
              {icon} {hideAmounts ? '******' : formatCurrency(Math.abs(dailyPnL))}
            </div>
            <div style={{ fontSize: '12px' }}>
              ({quantity.toLocaleString()}주)
            </div>
          </div>
        );
      },
    },
    {
      title: '평가손익',
      dataIndex: 'unrealized_pnl',
      key: 'unrealized_pnl',
      align: 'right',
      render: (value, record) => {
        if (!value && value !== 0) return '-';
        
        const isProfit = value >= 0;
        const color = isProfit ? '#52c41a' : '#ff4d4f';
        const icon = isProfit ? <RiseOutlined /> : <FallOutlined />;
        
        // 수익률 계산
        const profitRate = record.avg_buy_price > 0 
          ? (value / (record.quantity * record.avg_buy_price)) * 100 
          : 0;
        
        return (
          <div style={{ color }}>
            <div style={{ fontWeight: 'bold' }}>
              {icon} {hideAmounts ? '******' : formatCurrency(value)}
            </div>
            <div style={{ fontSize: '12px' }}>
              {hideAmounts ? '***' : formatPercentage(profitRate)}
            </div>
          </div>
        );
      },
    },
  ];

  // 계좌 요약 정보 계산
  const getAccountSummary = () => {
    if (!holdingsData?.holdings || holdingsData.holdings.length === 0) {
      return {
        totalValue: 0,
        totalPnL: 0,
        totalPnLRate: 0,
        stockCount: 0,
      };
    }

    // console.log('Holdings data for summary:', holdingsData.holdings); // 디버깅용

    const totalValue = holdingsData.holdings.reduce((sum, holding) => {
      const marketValue = parseFloat(holding.market_value) || 0;
      // console.log(`Market value for ${holding.stock_name}:`, marketValue); // 디버깅용
      return sum + marketValue;
    }, 0);
    
    const totalPnL = holdingsData.holdings.reduce((sum, holding) => {
      const unrealizedPnl = parseFloat(holding.unrealized_pnl) || 0;
      // console.log(`Unrealized PnL for ${holding.stock_name}:`, unrealizedPnl); // 디버깅용
      return sum + unrealizedPnl;
    }, 0);
    
    const totalCost = holdingsData.holdings.reduce((sum, holding) => {
      const quantity = parseInt(holding.quantity) || 0;
      const avgPrice = parseFloat(holding.avg_buy_price) || 0;
      const cost = quantity * avgPrice;
      // console.log(`Cost for ${holding.stock_name}:`, cost); // 디버깅용
      return sum + cost;
    }, 0);
    
    const totalPnLRate = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;

    // console.log('Summary calculation:', { totalValue, totalPnL, totalCost, totalPnLRate }); // 디버깅용

    return {
      totalValue: isNaN(totalValue) ? 0 : totalValue,
      totalPnL: isNaN(totalPnL) ? 0 : totalPnL,
      totalPnLRate: isNaN(totalPnLRate) ? 0 : totalPnLRate,
      stockCount: holdingsData.holdings.length,
    };
  };

  const summary = getAccountSummary();
  const selectedAccountInfo = accounts.find(acc => acc.id === selectedAccount);

  if (error) {
    return (
      <Card>
        <Alert
          message="오류"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div>
      {/* 계좌 선택 드롭다운 */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BankOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <Text strong>계좌 선택:</Text>
        </div>
        <Select
          value={selectedAccount}
          onChange={handleAccountChange}
          style={{ width: 250 }}
          placeholder="계좌를 선택해주세요"
        >
          {accounts.map(account => (
            <Option key={account.id} value={account.id}>
              {account.account_name} ({account.broker})
            </Option>
          ))}
        </Select>
      </div>

      {/* 계좌 정보 카드 */}
      {/* 계좌 요약 정보 */}
      {selectedAccountInfo && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Statistic
              title="총 평가금액"
              value={hideAmounts ? '******' : summary.totalValue}
              formatter={(value) => hideAmounts ? value : formatCurrency(value)}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic
              title="평가손익"
              value={hideAmounts ? '******' : summary.totalPnL}
              formatter={(value) => hideAmounts ? value : formatCurrency(value)}
              valueStyle={{ 
                color: summary.totalPnL >= 0 ? '#52c41a' : '#ff4d4f',
                fontSize: '18px'
              }}
              prefix={summary.totalPnL >= 0 ? <RiseOutlined /> : <FallOutlined />}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic
              title="수익률"
              value={hideAmounts ? '***' : summary.totalPnLRate}
              formatter={(value) => hideAmounts ? value : formatPercentage(value)}
              valueStyle={{ 
                color: summary.totalPnLRate >= 0 ? '#52c41a' : '#ff4d4f',
                fontSize: '18px'
              }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Statistic
              title="보유종목"
              value={summary.stockCount}
              suffix="개"
              valueStyle={{ color: '#722ed1', fontSize: '18px' }}
            />
          </Col>
        </Row>
      )}

      {/* 보유 종목 테이블 */}
      <div style={{ marginTop: 16 }}>
        <Spin 
          spinning={loading} 
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
        >
          <Table
            columns={columns}
            dataSource={holdingsData?.holdings || []}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total) => `총 ${total}개 종목`,
            }}
            locale={{ 
              emptyText: selectedAccount ? '보유 중인 종목이 없습니다.' : '계좌를 선택해주세요.'
            }}
            size="small"
          />
        </Spin>
      </div>
    </div>
  );
};

export default AccountHoldingsCard;
